/* eslint-disable no-shadow */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-use-before-define */
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as queryString from 'query-string';

import PopOverMenu from '../../PopOverMenu';
import Round from './Round';
import Standings from './Standings';
import TabBar from '../../TabBar';
import { findDrawById } from '../../../utils';
import { renderRoundLabel, drawStandingsEnabled } from '../../../utils/events';
import { populateChildDrawOptions, findUserDrawIds, findUserDraws } from '../../../utils/draws';
import { setDrawsTabState, getDrawOfficials, getDrawPlayerContactInfo } from '../../../actions/event-actions';

import styles from '../../../pages/EventProfiles/event.scss';
import { drawEliminationTypeIds } from '../../../utils/eventConstants';

const isCutOffRound = (draw, roundIndex) => {
    if (draw.eliminationTypeId === drawEliminationTypeIds.FEED_IN_BACK_DRAW) {
        return false;
    }

    return draw.feedOutRound && roundIndex + 1 > draw.feedOutRound;
};

const DrawsTabContent = (props) => {
    const { isCurrentMemberAdmin, 'data-testid': dataTestId } = props;
    const { auth, events } = useSelector((state) => state);
    const dispatch = useDispatch();
    const tabBarRef = useRef(null);

    const { currentEvent } = events;
    const { playersDict, teamsDict, data: event = {}, drawsTabState, drawsFetchState } = currentEvent;
    const { selectedDraw, selectedParentDraw, childDraws, activeRound, showStandings } = drawsTabState || {};
    const parsed = queryString.parse(props.location.search);

    const [stats, setStats] = useState({});
    const [tabs, setTabs] = useState([]);
    const [userDrawIds, setUserDrawIds] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [standingsEnabled, setStandingsEnabled] = useState();

    const allPlayersDict = { ...playersDict };
    event.withdrawnPlayers.forEach(player => {
        allPlayersDict[player.id] = player;
    });

    const firstPublishedDraw = (event?.draws?.find(draw => draw.published)) || null;

    const drawStandingsLoading = selectedDraw && stats[selectedDraw.id] && stats[selectedDraw.id].loading;
    const drawStandings = selectedDraw && stats[selectedDraw.id] && stats[selectedDraw.id].data;

    useEffect(() => {
        findUserDraw();
    }, []);

    useEffect(() => {
        populateTabs();
        // fix for mobile web view - to load contact info after init
        if (event.id && selectedDraw?.id) {
            dispatch(getDrawOfficials(event.id, selectedDraw?.id));
            dispatch(getDrawPlayerContactInfo(event.id, selectedDraw?.id));
        }
    }, [selectedDraw]);

    const setTabBarRef = (node) => {
        tabBarRef.current = node;
    };

    const findUserDraw = () => {
        const profilePlayerId = auth.user?.claimedPlayer?.playerId;
        const drawsTabStateUpdate = { ...drawsTabState };

        // find the user as a registered player
        const foundUserAsRegisteredPlayer = event.registeredPlayers.find(registeredPlayer => registeredPlayer.id === profilePlayerId && registeredPlayer.draws !== null);

        if (foundUserAsRegisteredPlayer) {
            // returns an array of draw id's the user is placed in
            const foundUserDrawIds = findUserDrawIds(profilePlayerId, foundUserAsRegisteredPlayer, event);
            setUserDrawIds(foundUserDrawIds);

            // returns an array of the respective draw object, and filter by published
            const allFoundDraws = findUserDraws(event.draws, foundUserDrawIds).filter(draw => draw.published);

            if (allFoundDraws.length) {
                // sort all alphabetically
                const alphabeticalDraws = allFoundDraws.sort((a, b) => a.name?.toLowerCase() - b.name?.toLowerCase());
                let drawToDisplay;
                // if a singles draw is found, pick the first one (already sorted alphabetically)
                const foundSinglesDraw = alphabeticalDraws.find(draw => draw.drawFormatType.value === 'singles');
                if (foundSinglesDraw) {
                    drawToDisplay = foundSinglesDraw;
                } else {
                    // If singles is not found, return first doubles in alphabetical order
                    drawToDisplay = alphabeticalDraws[0];
                }

                drawsTabStateUpdate.selectedParentDraw = drawToDisplay;
                if (drawToDisplay.drawCategoryType?.value === 'grouped') {
                    drawsTabStateUpdate.selectedDraw = drawToDisplay.childDraws.find(cd => foundUserDrawIds.includes(cd.id));
                } else {
                    drawsTabStateUpdate.selectedDraw = drawToDisplay;
                }
                drawsTabStateUpdate.childDraws = populateChildDrawOptions(drawToDisplay);
                drawsTabStateUpdate.activeRound = 0;
                updateDrawsTabState(drawsTabStateUpdate);
            }
            if (drawsTabStateUpdate.selectedDraw) {
                dispatch(getDrawOfficials(event?.id, drawsTabStateUpdate.selectedDraw.id));
                dispatch(getDrawPlayerContactInfo(event?.id, drawsTabStateUpdate.selectedDraw.id));
            }
        }
    };

    const populateTabs = () => {
        const standingsEnabledUpdate = drawStandingsEnabled(selectedDraw?.eliminationType?.value);
        const tabsUpdate = [];
        if (standingsEnabledUpdate) {
            tabsUpdate.push({
                title: 'Standings',
                showTab: true,
                active: showStandings,
            });
        }

        if (selectedDraw) {
            selectedDraw.rounds.forEach((round, i) => {
                if (!isCutOffRound(selectedDraw, i)) {
                    tabsUpdate.push({
                        title: renderRoundLabel(selectedDraw.eliminationType ? selectedDraw.eliminationType.value : 'default', round, false, selectedDraw.drawCategoryType ? selectedDraw.drawCategoryType.value : 'default'),
                        titleName: selectedDraw.eliminationType ? selectedDraw.eliminationType.label : 'default',
                        showTab: true,
                        active: !showStandings && activeRound === i,
                    });
                }
            });
        }
        setTabs(tabsUpdate);
        setStandingsEnabled(standingsEnabledUpdate);
    };

    const handleParentDrawSelect = (selectedParentDraw) => {
        const drawsTabStateUpdate = { ...drawsTabState };
        event.draws.forEach((draw) => {
            if (draw.id === selectedParentDraw.value) {
                drawsTabStateUpdate.selectedParentDraw = draw;
                if (draw.drawCategoryType?.value === 'grouped') {
                    drawsTabStateUpdate.selectedDraw = findDrawById(event.draws, draw.childDraws[0].id);
                } else {
                    drawsTabStateUpdate.selectedDraw = draw;
                }
                drawsTabStateUpdate.childDraws = populateChildDrawOptions(draw);
                drawsTabStateUpdate.activeRound = 0;
                updateDrawsTabState(drawsTabStateUpdate);
            }
        });
        if (drawsTabStateUpdate.selectedParentDraw.drawCategoryType?.value === 'grouped') {
            dispatch(getDrawOfficials(event.id, drawsTabStateUpdate.selectedParentDraw.childDraws[0].id));
            dispatch(getDrawPlayerContactInfo(event.id, drawsTabStateUpdate.selectedParentDraw.childDraws[0].id));
        } else {
            dispatch(getDrawOfficials(event.id, drawsTabStateUpdate.selectedParentDraw.id));
            dispatch(getDrawPlayerContactInfo(event.id, drawsTabStateUpdate.selectedParentDraw.id));
        }
    };

    const handleChildDrawSelect = (selectedDraw) => {
        const drawsTabStateUpdate = { ...drawsTabState };
        drawsTabStateUpdate.selectedDraw = findDrawById(event.draws, selectedDraw.value);
        drawsTabStateUpdate.activeRound = 0;
        if (drawsTabStateUpdate.selectedParentDraw.drawCategoryType?.value === 'grouped') {
            dispatch(getDrawOfficials(event.id, selectedDraw.value));
            dispatch(getDrawPlayerContactInfo(event.id, selectedDraw.value));
        }
        updateDrawsTabState(drawsTabStateUpdate);
    };

    const handleDrawsTabClick = (ev, i) => {
        const drawsTabStateUpdate = { ...drawsTabState };
        if (standingsEnabled) {
            drawsTabStateUpdate.activeRound = i === 0 ? 0 : i - 1;
            drawsTabStateUpdate.showStandings = i === 0;
        } else {
            drawsTabStateUpdate.activeRound = i;
        }
        updateDrawsTabState(drawsTabStateUpdate);
        const tabsUpdate = tabs.map((tab, j) => (i === j ? { ...tab, active: true } : { ...tab, active: false }));
        setTabs(tabsUpdate);
    };

    const printDraw = (drawId) => {
        if (drawId) {
            if (window) {
                window.open(`/events/${event.id}/draws/${drawId}/print`, '_blank');
            } else {
                props.history.push(`/events/${event.id}/draws/${drawId}/print`);
            }
        }
    };

    const updateDrawStats = ({ stats }) => {
        setStats(stats);
    };

    const updateDrawsTabState = (drawsTabState) => {
        parsed.d = drawsTabState.selectedDraw.id;
        parsed.r = drawsTabState.activeRound || 0;
        parsed.standings = drawsTabState.showStandings ? '1' : undefined;
        props.history.replace({
            search: queryString.stringify(parsed),
        });
        dispatch(setDrawsTabState(drawsTabState));
    };

    const getChildDraws = () => {
        if (selectedParentDraw.drawCategoryType.value === 'grouped') {
            return childDraws.map(({ value, label }) => {
                return {
                    value,
                    label,
                    playing: userDrawIds?.includes(value),
                };
            });
        }
        return [
            {
                value: selectedParentDraw.id,
                label: selectedParentDraw.drawCategoryType.label,
            },
            ...(childDraws),
        ];
    };

    if (drawsFetchState?.loading) {
        return (
            <div className="row">
                <div className="col-md-8">
                    <h6 className="text-uppercase" data-testid={dataTestId ? `${dataTestId}.loading-title` : undefined}>Draws</h6>
                    <p data-testid={dataTestId ? `${dataTestId}.loading-text` : undefined}>Loading Draws</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {firstPublishedDraw && (
                <div className="row">
                    <div className="col-md-8 d-flex">
                        {(event?.draws.length === 1) && (
                            <h6 className="text-uppercase" data-testid={dataTestId ? `${dataTestId}.draw-name` : undefined}>{firstPublishedDraw.name}</h6>
                        )}
                        {(event?.draws.length > 1) && (
                            <PopOverMenu
                                menuItems={event.draws.map(draw => {
                                    return draw.published ? {
                                        value: draw.id,
                                        label: draw.name,
                                        playing: userDrawIds.includes(draw.id) || draw.childDraws?.some(cd => userDrawIds.includes(cd.id)),
                                    } : null;
                                })}
                                onSelected={handleParentDrawSelect}
                                alignLeft={true}
                            >
                                <h6 className={`${styles.anchorClass} text-uppercase pl8`} data-testid={dataTestId ? `${dataTestId}.draw-selector` : undefined}>
                                    {drawsTabState && selectedParentDraw?.name} <i className={`${styles.arrow} uts uts-expand fs18`} />
                                </h6>
                            </PopOverMenu>
                        )}
                        {drawsTabState && selectedParentDraw?.childDraws?.length > 0 && (
                            <h6 className="border-left text-medium text-lighter ml16 pl16">
                                <PopOverMenu
                                    anchorClass={styles.anchorClass}
                                    popOverMenuClass="flex-right"
                                    menuItems={getChildDraws()}
                                    onSelected={handleChildDrawSelect}
                                    alignLeft={true}
                                >
                                    <span className="pl8">({selectedParentDraw?.drawCategoryType?.value === 'grouped' ? selectedDraw?.name : selectedDraw.drawCategoryType.label})</span> <i className={`${styles.arrow} uts uts-expand fs18`} />
                                </PopOverMenu>
                            </h6>
                        )}
                    </div>
                    <div className="col-md-4 d-none d-md-block">
                        <h6 className="text-right">
                            {drawsTabState && selectedParentDraw && auth?.user && (
                                <button
                                    type="button"
                                    className="btn btn-small btn-default float-right"
                                    onClick={() => printDraw(selectedParentDraw.id)}
                                    data-testid={dataTestId ? `${dataTestId}.print-btn` : undefined}
                                >
                                    <i className="fas fa-print" /> Print
                                </button>
                            )}
                        </h6>
                    </div>
                </div>
            )}
            {!firstPublishedDraw && (
                <div className="row">
                    <div className="col-md-8">
                        <h6 className="text-uppercase" data-testid={dataTestId ? `${dataTestId}.no-draws-title` : undefined}>Draws</h6>
                        <p data-testid={dataTestId ? `${dataTestId}.no-draws-message` : undefined}>
                            Draws haven’t been published<br />
                            Check back to view draws once published.
                        </p>
                    </div>
                </div>
            )}
            {firstPublishedDraw && drawsTabState && selectedDraw?.rounds.length && (
                <div className={`${styles.section} bg-transparent main-content`}>
                    <TabBar
                        tabs={tabs}
                        handleTabClick={handleDrawsTabClick}
                        ref={setTabBarRef}
                        eventName="Draws Tab Clicked"
                        transparent={true}
                        noContainer={true}
                        clickCategory="Event Profile"
                        data-testid="event-profile.draws-legacy.tab-bar"
                        clickLocation="Draws Tab"
                    />
                    {!(showStandings && standingsEnabled) && (
                        <div className={`container bg-transparent matches`}>
                            <div className="row">
                                <Round
                                    selectedDraw={selectedDraw}
                                    round={activeRound}
                                    teamsDict={teamsDict}
                                    playersDict={playersDict}
                                    isCurrentMemberAdmin={isCurrentMemberAdmin}
                                    utrType={event.utrType}
                                    eventSchedule={event.eventSchedule}
                                    columnNumber={1}
                                    displayOnMobile={true}
                                    showMatchDates={true}
                                    events={events}
                                />
                                {selectedDraw.rounds[activeRound + 1] && selectedDraw.rounds[activeRound + 1].matches && !isCutOffRound(selectedDraw, activeRound + 1) && (
                                    <Round
                                        selectedDraw={selectedDraw}
                                        round={activeRound + 1}
                                        teamsDict={teamsDict}
                                        playersDict={playersDict}
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        utrType={event.utrType}
                                        eventSchedule={event.eventSchedule}
                                        showMatchDates={true}
                                        columnNumber={2}
                                        events={events}
                                    />
                                )}
                                {selectedDraw.rounds[activeRound + 2] && selectedDraw.rounds[activeRound + 2].matches && !isCutOffRound(selectedDraw, activeRound + 2) && (
                                    <Round
                                        selectedDraw={selectedDraw}
                                        round={activeRound + 2}
                                        teamsDict={teamsDict}
                                        playersDict={playersDict}
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        utrType={event.utrType}
                                        eventSchedule={event.eventSchedule}
                                        showMatchDates={true}
                                        columnNumber={3}
                                        events={events}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    <br />
                    {(showStandings && standingsEnabled) && (
                        <div className={`container bg-transparent matches`}>
                            <div className="row">
                                <div className="col">
                                    <Standings
                                        drawStandingsLoading={drawStandingsLoading}
                                        drawStandings={drawStandings}
                                        playersDict={allPlayersDict}
                                        teamsDict={teamsDict}
                                        event={event}
                                        drawsTabState={drawsTabState}
                                        stats={stats}
                                        updateDrawStats={updateDrawStats}
                                        parsed={parsed}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default withRouter(DrawsTabContent);
